import { z } from 'zod';

/** Fontes permitidas (lista fechada de Google Fonts). */
export const STORE_FONTS = ['Inter', 'Poppins', 'Montserrat', 'Lora', 'Playfair Display', 'Nunito', 'Roboto'] as const;
export const BUTTON_STYLES = ['rounded', 'square', 'pill'] as const;

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use uma cor no formato #RRGGBB.');

export const storeSectionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hero'),
    title: z.string().max(80).default('Bem-vindo à nossa loja'),
    subtitle: z.string().max(160).default(''),
    imageKey: z.string().max(300).nullable().default(null),
    ctaLabel: z.string().max(30).default('Ver produtos'),
    ctaLink: z.string().max(200).default('/produtos'),
  }),
  z.object({ type: z.literal('featured-products'), title: z.string().max(60).default('Destaques'), limit: z.number().int().min(2).max(24).default(8) }),
  z.object({ type: z.literal('categories'), title: z.string().max(60).default('Categorias') }),
  z.object({ type: z.literal('banner') }),
  z.object({
    type: z.literal('testimonials'),
    title: z.string().max(60).default('Quem comprou, recomenda'),
    items: z.array(z.object({ name: z.string().max(60), text: z.string().max(300) })).max(12).default([]),
  }),
  z.object({ type: z.literal('whatsapp-cta'), text: z.string().max(120).default('Fale com a gente no WhatsApp') }),
]);
export type StoreSection = z.infer<typeof storeSectionSchema>;
export const STORE_SECTION_TYPES = ['hero', 'featured-products', 'categories', 'banner', 'testimonials', 'whatsapp-cta'] as const;

function themeSchema(defaults: { colors: { primary: string; secondary: string; background: string; text: string }; font: (typeof STORE_FONTS)[number]; buttonStyle: (typeof BUTTON_STYLES)[number]; sections: StoreSection['type'][] }) {
  return z.object({
    colors: z
      .object({
        primary: hex.default(defaults.colors.primary),
        secondary: hex.default(defaults.colors.secondary),
        background: hex.default(defaults.colors.background),
        text: hex.default(defaults.colors.text),
      })
      .prefault({}),
    font: z.enum(STORE_FONTS).default(defaults.font),
    buttonStyle: z.enum(BUTTON_STYLES).default(defaults.buttonStyle),
    /** Seções da home, em ordem. */
    sections: z
      .array(storeSectionSchema)
      .max(20)
      .default(defaults.sections.map((type) => storeSectionSchema.parse({ type }))),
  });
}

/**
 * Temas: o schema tem valores padrão, então um tema novo ou um campo novo nunca quebra
 * a configuração salva de uma loja existente.
 */
export const STORE_THEMES = {
  essencial: {
    name: 'Essencial',
    description: 'Limpo e direto: destaque para os produtos.',
    schema: themeSchema({
      colors: { primary: '#1f6feb', secondary: '#0f172a', background: '#ffffff', text: '#111827' },
      font: 'Inter',
      buttonStyle: 'rounded',
      sections: ['hero', 'featured-products', 'categories', 'whatsapp-cta'],
    }),
  },
  vitrine: {
    name: 'Vitrine',
    description: 'Imagens grandes e banners, para quem vende pelo visual.',
    schema: themeSchema({
      colors: { primary: '#be185d', secondary: '#1f2937', background: '#fffaf5', text: '#1f2937' },
      font: 'Playfair Display',
      buttonStyle: 'pill',
      sections: ['hero', 'banner', 'featured-products', 'testimonials', 'categories', 'whatsapp-cta'],
    }),
  },
} as const;
export type StoreThemeKey = keyof typeof STORE_THEMES;
export const STORE_THEME_KEYS = Object.keys(STORE_THEMES) as StoreThemeKey[];
export type ThemeConfig = z.infer<(typeof STORE_THEMES)['essencial']['schema']>;

/** Configuração salva → configuração completa (com padrões do tema). Inválida → padrão. */
export function resolveThemeConfig(themeKey: string, config: unknown): ThemeConfig {
  const theme = STORE_THEMES[themeKey as StoreThemeKey] ?? STORE_THEMES.essencial;
  const parsed = theme.schema.safeParse(config ?? {});
  return parsed.success ? parsed.data : theme.schema.parse({});
}

/**
 * Troca de tema sem perder o que a loja configurou: o que foi personalizado (cores, fonte, botões,
 * seções e textos) continua igual; o que ainda está no padrão do tema antigo passa para o padrão
 * do novo, para a troca ainda mudar a cara da loja.
 */
export function switchThemeConfig(fromKey: StoreThemeKey, config: ThemeConfig, toKey: StoreThemeKey): ThemeConfig {
  const from = STORE_THEMES[fromKey].schema.parse({}) as ThemeConfig;
  const to = STORE_THEMES[toKey].schema.parse({}) as ThemeConfig;
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const pick = <T>(current: T, oldDefault: T, newDefault: T) => (same(current, oldDefault) ? newDefault : current);
  return {
    ...config,
    colors: {
      primary: pick(config.colors.primary, from.colors.primary, to.colors.primary),
      secondary: pick(config.colors.secondary, from.colors.secondary, to.colors.secondary),
      background: pick(config.colors.background, from.colors.background, to.colors.background),
      text: pick(config.colors.text, from.colors.text, to.colors.text),
    },
    font: pick(config.font, from.font, to.font),
    buttonStyle: pick(config.buttonStyle, from.buttonStyle, to.buttonStyle),
    sections: pick(config.sections, from.sections, to.sections),
  };
}

export const publishThemeSchema = z.object({
  themeKey: z.enum(STORE_THEME_KEYS as [StoreThemeKey, ...StoreThemeKey[]]),
  themeConfig: z.record(z.string(), z.unknown()),
});

export const storeSettingsSchema = z
  .object({
    seo: z.object({ title: z.string().max(70).optional(), description: z.string().max(160).optional() }),
    policies: z.object({
      exchanges: z.string().max(10_000).optional(),
      privacy: z.string().max(20_000).optional(),
      shipping: z.string().max(10_000).optional(),
    }),
    socialLinks: z.object({
      instagram: z.string().url().optional(),
      facebook: z.string().url().optional(),
      tiktok: z.string().url().optional(),
    }),
    whatsappNumber: z.string().trim().max(20).nullable(),
  })
  .partial();

export const storePageSchema = z.object({
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use só letras minúsculas, números e hífen.')
    .max(60),
  title: z.string().trim().min(2).max(100),
  content: z.array(z.object({ type: z.enum(['heading', 'paragraph', 'image', 'list']), text: z.string().max(10_000).optional(), imageKey: z.string().max(300).optional(), items: z.array(z.string().max(500)).max(50).optional() })).max(200),
  published: z.boolean().default(false),
});

export const bannerSchema = z.object({
  imageKey: z.string().min(5).max(300),
  link: z.string().max(300).nullable().optional(),
  position: z.coerce.number().int().min(0).default(0),
  activeFrom: z.coerce.date().nullable().optional(),
  activeTo: z.coerce.date().nullable().optional(),
});

export const addDomainSchema = z.object({
  hostname: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/, 'Domínio inválido. Ex.: loja.meunegocio.com.br'),
});

export const publicProductsQuerySchema = z.object({
  category: z.string().max(80).optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(48).default(24),
});
